import {defineStore} from "pinia";
import {piniaInstance} from "@/stores/global";
import {FlowUnit} from "@/models/FlowUnit";
import {Area} from "@/models/Area";
import {Currency} from "@/models/Currency";
import {PressureUnit} from "@/models/PressureUnit";
import {ref} from "vue";
import {useStorage} from "@vueuse/core";
import {PipeMass} from "@/models/PipeMass";

const defaultValues: any = {
	flowUnits: new Array<FlowUnit>(),
	currencies: new Array<Currency>(),
	pressureUnits: new Array<PressureUnit>(),
	areas: new Array<Area>(),
};

const stateUserCustomizations = useStorage('user_customizations', ref(defaultValues));

const addItemsToArray = (targetArray: Array<any>, itemsToAdd: Array<any>) => {
	let arToAdd = [], dummyIndex: number = -1;
	for (let i = 0, itemNew: any, itemPresent: boolean; i < itemsToAdd.length; i++) {
		itemNew = itemsToAdd[i];
		itemPresent = false;
		for (let j = 0, itemOld; j < targetArray.length; j++) {
			itemOld = targetArray[j];
			if (itemOld['_id'] === '') {
				dummyIndex = j;
			}
			if (itemOld['_id'] === itemNew['_id']) {
				targetArray[j] = itemNew;
				itemPresent = true;
			}
		}
		if (dummyIndex > -1) {
			targetArray.splice(dummyIndex, 1);
		}
		if (!itemPresent) {
			arToAdd.push(itemNew);
		}
	}
	return arToAdd.concat(targetArray);
}

const useAppConfigStore = defineStore('globalConfig', {
	state: () => ({
		isLoading: true,
		loadedLanguage: 'de',
		texts: {
			alerts: {
				required: '',
				minLength3: '',
				selectArea: '',
				insertValue: '',
				insertNumericValue: '',
				validEmail: '',
			},
			appSettings: {
				dialogTitle: '',
				tab_1: '',
				tab_2: '',
				tab_3: '',
				tab_4: '',
				label_1: '',
				label_2: '',
				label_3: '',
				label_4: '',
				label_5: '',
				label_6: '',
				label_7: '',
			},
			buttons: {
				closeBtn: '',
				createPDFbtn: '',
				nextBtn: '',
				previousBtn: '',
				deleteChosenBtn: '',
				saveBtn: '',
				unitsBtn: '',
			},
			columnHeaders: {
				percentChange: '',
				value: '',
				comments: '',
			},
			mainMenu: {
				menuItem_1: '',
				menuItem_2: '',
				menuItem_3: '',
				menuItem_4: '',
				menuItem_5: '',
				menuItem_6: '',
			},
			introduction: {
				menuText: '',
				text: '',
				title: '',
				buttonText: ''
			},
			settings: {
				information: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
					label_6: '',
					label_7: '',
				},
				electricityMix: {
					menuName: '',
					title: '',
					hint: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
					label_6: '',
					label_7: '',
					label_8: '',
				},
				distributionOfWaterSources: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
				},
			},
			input: {
				authorizedConsumption: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
				},
				calculationPeriod: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
				},
				emissionAndElectricityData: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
				},
				leakage: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
				},
				nonRevenueWater: {
					menuName: '',
					title: '',
					label_1: '',
				},
				pipelineLifetime: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
					label_6: '',
					label_7: '',
					label_8: '',
				},
				pressureData: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
				},
				systemData: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
					label_6: '',
					label_7: '',
				},
				waterLosses: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
					label_6: '',
					label_7: '',
				},
				waterSupplied: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
				},
			},
			pressureManagement: {
				effectsOnPipelineLifetime: {
					menuName: '',
					title: '',
					infotext: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
					label_6: '',
					label_7: '',
					label_8: '',
					label_9: '',
					label_10: '',
					label_11: '',
				},
				effectsOnRevenueCostsAndCo2Emissions: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
					label_6: '',
					label_7: '',
					label_8: '',
				},
				leakageLevelWaterConsumptionAndElectricityConsumption: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
				},
				pressuremanagementInput: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
				},
			},
			pipeBursts: {
				burstsAfterPressureManagement: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
					label_6: '',
				},
				effectsOnRevenueCostsAndCo2EmissionsPipeBursts: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
					label_6: '',
					label_7: '',
					label_8: '',
					label_9: '',
					label_10: '',
				},
				pipeBurstsBeforePressureManagement: {
					menuName: '',
					title: '',
					label_1: '',
					label_2: '',
					label_3: '',
					label_4: '',
					label_5: '',
				},
			},
			results: {
				menuName: '',
				title: '',
				explanation: '',
				subheader_1: '',
				explanation_table_1: '',
				header_table_1: '',
				tab_1_col_1: '',
				tab_1_col_2: '',
				tab_1_col_3: '',
				tab_1_col_4: '',
				tab_1_row_1: '',
				tab_1_row_2: '',
				tab_1_row_3: '',
				explanation_chart12: '',
				chart_1_title: '',
				chart_2_title: '',
				explanation_table_2: "",
				header_table_2: "",
				tab_2_col_1: '',
				tab_2_col_2: '',
				tab_2_col_3: '',
				tab_2_col_4: '',
				tab_2_col_5: '',
				tab_2_col_6: '',
				tab_2_row_1: '',
				tab_2_row_2: '',
				tab_2_row_3: '',
				chart_3_title: '',
				chart_4_title: '',
				explanation_table_3: "",
				header_table_3: "",
				tab_3_col_1: '',
				tab_3_col_2: '',
				tab_3_col_3: '',
				tab_3_col_4: '',
				tab_3_col_5: '',
				tab_3_row_1: '',
				tab_3_row_2: '',
				tab_3_row_3: '',
				title_background_data: "",
				explanation_background_data: "",
				header_table_4: "",
				tab_4_row_1: '',
				tab_4_row_2: '',
				tab_4_row_3: '',
				tab_4_row_4: '',
				header_table_5: "",
				tab_5_col_1: '',
				tab_5_col_2: '',
				tab_5_col_3: '',
				tab_5_col_4: '',
				tab_5_row_1: '',
				tab_5_row_2: '',
				tab_5_row_3: '',
				tab_5_row_4: '',
				tab_5_row_5: '',
				title_effects_of_pm: "",
				explanation_table_6: "",
				header_table_6: "",
				tab_6_col_1: '',
				tab_6_col_2: '',
				tab_6_col_3: '',
				tab_6_col_4: '',
				tab_6_row_1: '',
				tab_6_row_2: '',
				tab_6_row_3: '',
				tab_6_row_4: '',
				tab_6_row_5: '',
				tab_6_row_6: '',
				tab_6_row_7: '',
				tab_6_row_8: '',
				tab_6_row_9: '',
				tab_6_row_10: '',
				title_description_neoflow: "",
				description_neoflow: "",
			},
			tooltips: {
				electricity_mix_area: '',
				water_supplied: '',
				authorized_consumption: '',
				water_losses_estimated_inaccuracies: '',
				system_data_infrastructure_condition_factor: '',
				leakage_ili: '',
				pressure_management_n3factor: '',
				leakage_level_after_change_in_pressure: '',
				expected_change_in_reported_bursts_in_mains_and_service_connections: '',
			},
		},

		flowUnits: stateUserCustomizations.value['flowUnits'].length > 0 ? stateUserCustomizations.value['flowUnits'] : ref(new Array<FlowUnit>(new FlowUnit("", "", 0, ''))),

		currencies: stateUserCustomizations.value['currencies'].length > 0 ? stateUserCustomizations.value['currencies'] : ref(new Array<Currency>(new Currency("", "", 0))),

		pressureUnits: stateUserCustomizations.value['pressureUnits'].length > 0 ? stateUserCustomizations.value['pressureUnits'] : ref(new Array<PressureUnit>(new PressureUnit("", "", 0, ''))),

		areas: stateUserCustomizations.value['areas'].length > 0 ? stateUserCustomizations.value['areas'] : ref(new Array<Area>(new Area("", "", 0, 0, 0, 0, 0, 0, 0))),

		pipeMasses: ref(new Array<PipeMass>(new PipeMass("", 0, "", 0, 0, 0, 0))),

		constants: {
			kgCO2eqPerKWh_oil: 0.82,
			kgCO2eqPerKWh_coal: 0.914,
			kgCO2eqPerKWh_gas: 0.49,
			kgCO2eqPerKWh_biomass: 0.21,
			kgCO2eqPerKWh_renewables: 0.028714286,
			kgCO2eqPerKWh_nuclearpower: 0.012,
			kgCO2eqPerKWh_other: 0.35872316,
			kWhPerSqm_groundwater: 0.41,
			kWhPerSqm_surfacewater: 1.49,
			kWhPerSqm_desalination: 3.25,
			kgCO2eqPerSqmWater_gWsW_waterSupplyNetwork: 0.0833,
			kgCO2equivalentPerSqmWater_gWsW_production: 0.113,
			kgCO2equivalentPerSqmWater_gWsW_total: 0.1963,
			kgCO2eqPerSqmWater_desalination_waterSupplyNetwork: 0.0833,
			kgCO2eqPerSqmWater_desalination_production: 3.01,
			kgCO2eqPerSqmWater_desalination_total: 3.0933,
			kgCO2eqPerKg_PP: 2.04,
			kgCO2eqPerKg_PVC: 2.31,
			kgCO2eqPerKg_PE: 2.62,
			kgCO2eqPerKg_plastic: 2.32,
			kgCO2eqPerKg_concrete: 0.13,
			kgCO2eqPerKg_steel: 3.02,
			kgCO2eqPerKg_iron: 1.65,
			average_co2_emissions_from_cars: 122.1,
			assumed_average_km_per_year: 15000,
			gradient_mains: 29.64533567,
			gradient_service_connections: 11.8581012,
			intercept_mains: 26.89704409,
			intercept_service_connections: 10.75776553,
			potential_pipeline_lifespan_increase_plastic: 0.13125,
			potential_pipeline_lifespan_increase_iron: 0.051996451,
			potential_pipeline_lifespan_increase_asbestoscement: 0.07027027,
			potential_pipeline_lifespan_increase_steel: 0.187096774,
		},

		commonLocalizations: {
			burst: 'burst',
			bursts: 'bursts',
			dateFormat: 'DD.MM.yyyy',
			year: 'year',
			years: 'years',
		},
		error: {}
	}),
	getters: {
		getLoadingState: (state) => {
			return state.isLoading;
		},
		getIntroduction: (state) => {
			return state.texts.introduction;
		},
		getAlerts: (state) => {
			return state.texts.alerts;
		},
		getFlowUnits(state): FlowUnit[] {
			return state.flowUnits as FlowUnit[];
			// return this.flowUnits;
		},
		getAreas: (state) => {
			return state.areas as Area[];
		},
		getCurrencies: (state) => {
			return state.currencies as Currency[];
		},
		getPressureUnits: (state) => {
			return state.pressureUnits as PressureUnit[];
		},
		getPipeMasses: (state) => {
			return state.pipeMasses as PipeMass[];
		},
	},
	actions: {
		async fetchData(lang: string = 'de', callbackFunction: () => any) {
			console.log('loading lang:', lang);
			this.loadedLanguage = lang;
			this.isLoading = true;
			let query = `{
			  alertsModel(locale:"` + lang + `"){
				required
				minLength3
				selectArea
				insertValue
				insertNumericValue
				validEmail
			  }
			  buttonsModel(locale:"` + lang + `"){
				closeBtn
				createPDFbtn
				nextBtn
				previousBtn
				deleteChosenBtn
				saveBtn
				unitsBtn
			  }
			  columnHeadersModel(locale:"` + lang + `"){
				percentChange
				value
				comments
			  }
			  mainMenuModel(locale:"` + lang + `"){
				menuItem_1
				menuItem_2
				menuItem_3
				menuItem_4
				menuItem_5
				menuItem_6
			  }
			  appSettingsModel(locale:"` + lang + `"){
				dialogTitle
				tab_1
				tab_2
				tab_3
				tab_4
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
				label_7
			  }
			  authorizedConsumptionModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
			  }
			  burstsAfterPressureManagementModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
			  }
			  calculationPeriodModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
			  }
			  distributionOfWaterSourcesModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
			  }
			  effectsOnPipelineLifetimeModel(locale:"` + lang + `"){
				menuName
				title
				infotext
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
				label_7
				label_8
				label_9
				label_10
				label_11
			  }
			  effectsOnRevenueCostsAndCo2EmissionsModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
				label_7
				label_8
			  }
			  effectsOnRevenueCostsAndCo2EmissionsPipeBurstsModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
				label_7
				label_8
				label_9
				label_10
			  }
			  electricityMixModel(locale:"` + lang + `"){
				menuName
				title
				hint
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
				label_7
				label_8
			  }
			  emissionAndElectricityDataModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
			  }
			  informationModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
				label_7
			  }
			  introductionModel(locale:"` + lang + `"){
				menuName
				title
				text
				buttonText
			  }
			  leakageModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
			  }
			  leakageLevelWaterConsumptionAndElectricityConsumptionModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
			  }
			  nonRevenueWaterModel(locale:"` + lang + `"){
				menuName
				title
				label_1
			  }
			  pipeBurstsBeforePressureManagementModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
			  }
			  pipelineLifetimeModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
				label_7
				label_8
			  }
			  pressureDataModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
			  }
			  pressuremanagementInputModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
			  }
			  resultsModel(locale:"` + lang + `"){
				menuName
				title
			  	explanation
			  	subheader_1
			  	explanation_table_1
			  	header_table_1
			  	tab_1_col_1
			  	tab_1_col_2
			  	tab_1_col_3
			  	tab_1_col_4
			  	tab_1_row_1
			  	tab_1_row_2
			  	tab_1_row_3
			  	explanation_chart12
			  	chart_1_title
			  	chart_2_title
			  	explanation_table_2
			  	header_table_2
				tab_2_col_1
				tab_2_col_2
				tab_2_col_3
				tab_2_col_4
				tab_2_col_5
				tab_2_col_6
			  	tab_2_row_1
			  	tab_2_row_2
			  	tab_2_row_3
			  	chart_3_title
			  	chart_4_title
			  	explanation_table_3
			  	header_table_3
				tab_3_col_1
				tab_3_col_2
				tab_3_col_3
				tab_3_col_4
				tab_3_col_5
			  	tab_3_row_1
			  	tab_3_row_2
			  	tab_3_row_3
			  	title_background_data
			  	explanation_background_data
			  	header_table_4
			  	tab_4_row_1
			  	tab_4_row_2
			  	tab_4_row_3
			  	tab_4_row_4
			  	header_table_5
				tab_5_col_1
				tab_5_col_2
				tab_5_col_3
				tab_5_col_4
			  	tab_5_row_1
			  	tab_5_row_2
			  	tab_5_row_3
			  	tab_5_row_4
			  	tab_5_row_5
			  	title_effects_of_pm
			  	explanation_table_6
			  	header_table_6
				tab_6_col_1
				tab_6_col_2
				tab_6_col_3
				tab_6_col_4
			  	tab_6_row_1
			  	tab_6_row_2
			  	tab_6_row_3
			  	tab_6_row_4
			  	tab_6_row_5
			  	tab_6_row_6
			  	tab_6_row_7
			  	tab_6_row_8
			  	tab_6_row_9
			  	tab_6_row_10
			  	title_description_neoflow
			  	description_neoflow
			  }
			  systemDataModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
				label_7
			  }
			  tooltipsModel(locale:"` + lang + `"){
				electricity_mix_area
				water_supplied
				authorized_consumption
				water_losses_estimated_inaccuracies
				system_data_infrastructure_condition_factor
				leakage_ili
				pressure_management_n3factor
				leakage_level_after_change_in_pressure
				expected_change_in_reported_bursts_in_mains_and_service_connections
			  }
			  waterLossesModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
				label_5
				label_6
				label_7
			  }
			  waterSuppliedModel(locale:"` + lang + `"){
				menuName
				title
				label_1
				label_2
				label_3
				label_4
			  }
			  currencyModel(locale:"` + lang + `"){
				_id
				isoName
				exchangeRate
				comment
			  }
			  pressureUnitModel(locale:"` + lang + `"){
				_id
				unit
				factor
				comment
			  }
			  flowUnitModel(locale:"` + lang + `"){
				_id
				unit
				factor
				comment
			  }
			  constantsModel{
				kgCO2eqPerKWh_oil
				kgCO2eqPerKWh_coal
				kgCO2eqPerKWh_gas
				kgCO2eqPerKWh_biomass
				kgCO2eqPerKWh_renewables
				kgCO2eqPerKWh_nuclearpower
				kgCO2eqPerKWh_other
				kWhPerSqm_groundwater
				kWhPerSqm_surfacewater
				kWhPerSqm_desalination
				kgCO2eqPerSqmWater_gWsW_waterSupplyNetwork
				kgCO2equivalentPerSqmWater_gWsW_production
				kgCO2equivalentPerSqmWater_gWsW_total
				kgCO2eqPerSqmWater_desalination_waterSupplyNetwork
				kgCO2eqPerSqmWater_desalination_production
				kgCO2eqPerSqmWater_desalination_total
				kgCO2eqPerKg_PP
				kgCO2eqPerKg_PVC
				kgCO2eqPerKg_PE
				kgCO2eqPerKg_plastic
				kgCO2eqPerKg_concrete
				kgCO2eqPerKg_steel
				kgCO2eqPerKg_iron
				average_co2_emissions_from_cars
				assumed_average_km_per_year
				gradient_mains
				gradient_service_connections
				intercept_mains
				intercept_service_connections
				potential_pipeline_lifespan_increase_plastic
				potential_pipeline_lifespan_increase_iron
				potential_pipeline_lifespan_increase_asbestoscement
				potential_pipeline_lifespan_increase_steel
			  },
			  commonLocalizationsModel(locale:"` + lang + `"){
			  	burst
			  	bursts
				dateFormat
				year
				years
			  }
			  areaModel(locale:"` + lang + `"){
				_id
				areaName
				fromOil
				fromCoal
				fromGas
				fromBiomass
				fromRenewables
				fromNuclearpower
				fromOther
				status
				source
				link
			  }
			  pipeMassPerMeterModel(sort:sortId){
				_id
				sortId
				range
				iron
				steel
				plastic
				asbestos_cement
			  }
			}`
			try {
				let response = await fetch('https://neoflow.gfpstools.com/api/gql', {
					method: 'POST',
					headers: {
						'content-type': 'application/json'
					},
					body: JSON.stringify({query}),
				})
					.then(response => response.json());
				this.texts.alerts = response.data['alertsModel'];
				this.texts.appSettings = response.data['appSettingsModel'];
				this.texts.buttons = response.data['buttonsModel'];
				this.texts.columnHeaders = response.data['columnHeadersModel'];
				this.texts.mainMenu = response.data['mainMenuModel'];
				this.texts.introduction = response.data['introductionModel'];
				this.texts.settings.information = response.data['informationModel'];
				this.texts.settings.electricityMix = response.data['electricityMixModel'];
				this.texts.settings.distributionOfWaterSources = response.data['distributionOfWaterSourcesModel'];
				this.texts.input.authorizedConsumption = response.data['authorizedConsumptionModel'];
				this.texts.input.calculationPeriod = response.data['calculationPeriodModel'];
				this.texts.input.emissionAndElectricityData = response.data['emissionAndElectricityDataModel'];
				this.texts.input.leakage = response.data['leakageModel'];
				this.texts.input.nonRevenueWater = response.data['nonRevenueWaterModel'];
				this.texts.input.pipelineLifetime = response.data['pipelineLifetimeModel'];
				this.texts.input.pressureData = response.data['pressureDataModel'];
				this.texts.input.systemData = response.data['systemDataModel'];
				this.texts.input.waterLosses = response.data['waterLossesModel'];
				this.texts.input.waterSupplied = response.data['waterSuppliedModel'];
				this.texts.pressureManagement.effectsOnPipelineLifetime = response.data['effectsOnPipelineLifetimeModel'];
				this.texts.pressureManagement.effectsOnRevenueCostsAndCo2Emissions = response.data['effectsOnRevenueCostsAndCo2EmissionsModel'];
				this.texts.pressureManagement.pressuremanagementInput = response.data['pressuremanagementInputModel'];
				this.texts.pressureManagement.leakageLevelWaterConsumptionAndElectricityConsumption = response.data['leakageLevelWaterConsumptionAndElectricityConsumptionModel'];
				this.texts.pipeBursts.burstsAfterPressureManagement = response.data['burstsAfterPressureManagementModel'];
				this.texts.pipeBursts.effectsOnRevenueCostsAndCo2EmissionsPipeBursts = response.data['effectsOnRevenueCostsAndCo2EmissionsPipeBurstsModel'];
				this.texts.pipeBursts.pipeBurstsBeforePressureManagement = response.data['pipeBurstsBeforePressureManagementModel'];
				this.texts.results = response.data['resultsModel'];
				this.texts.tooltips = response.data['tooltipsModel'];
				this.flowUnits = addItemsToArray(this.flowUnits, response.data['flowUnitModel'] as FlowUnit[]);
				this.currencies = addItemsToArray(this.currencies, response.data['currencyModel'] as FlowUnit[]);
				this.pressureUnits = addItemsToArray(this.pressureUnits, response.data['pressureUnitModel'] as FlowUnit[]);
				this.areas = addItemsToArray(this.areas, response.data['areaModel'] as FlowUnit[]);
				this.pipeMasses = addItemsToArray(this.pipeMasses, response.data['pipeMassPerMeterModel'] as PipeMass[]);
				this.constants = response.data['constantsModel'];
				this.commonLocalizations = response.data['commonLocalizationsModel'];
			} catch (err) {
				return err;
			} finally {
				this.isLoading = false;
				callbackFunction();
			}
		},

		addFlowUnit(flowUnit: FlowUnit) {
			stateUserCustomizations.value['flowUnits'].push(flowUnit);
			this.flowUnits.push(flowUnit);
		},
		addCurrency(currency: Currency) {
			stateUserCustomizations.value['currencies'].push(currency);
			this.currencies.push(currency);
		},
		addPressureUnit(pressureUnit: PressureUnit) {
			stateUserCustomizations.value['pressureUnits'].push(pressureUnit);
			this.pressureUnits.push(pressureUnit);
		},
		addArea(area: Area) {
			stateUserCustomizations.value['areas'].push(area);
			this.areas.push(area);
		},
		deleteCustomItemFromLocalStorage(id: string) {
			for (let key in stateUserCustomizations.value) {
				console.log('key:', key);
				let ar: Array<any> = stateUserCustomizations.value[key] as Array<any>;
				for (let i = 0; i < ar.length; i++) {
					console.log('ar:', ar);
					if (ar[i]['_id'] === id) {
						ar.splice(i, 1);
						return;
					}
				}
			}
		}
	}

})(piniaInstance);

export default useAppConfigStore;
